import type { VoiceCallStatus } from '@/lib/twilio-voice';

export type TranscriptLiveHealth = {
  status: 'idle' | 'waiting' | 'live' | 'stale' | 'missing';
  label: 'Idle' | 'Waiting' | 'Live' | 'Delayed' | 'No Chunks';
  detail: string;
};

type ResolveTranscriptLiveHealthInput = {
  callStatus: VoiceCallStatus;
  hasActiveCall: boolean;
  latestTranscriptAt: string | null;
  nowMs?: number;
  staleAfterMs?: number;
};

const DEFAULT_STALE_AFTER_MS = 15_000;

export function resolveTranscriptLiveHealth(
  input: ResolveTranscriptLiveHealthInput,
): TranscriptLiveHealth {
  const staleAfterMs = Math.max(3_000, input.staleAfterMs ?? DEFAULT_STALE_AFTER_MS);
  const nowMs = input.nowMs ?? Date.now();
  const isCallExpectedLive =
    input.hasActiveCall && (input.callStatus === 'calling' || input.callStatus === 'in-progress');

  if (!isCallExpectedLive) {
    return {
      status: 'idle',
      label: 'Idle',
      detail:
        'Transcript updates are generated during active calls and visible from timeline events.',
    };
  }

  if (!input.latestTranscriptAt) {
    if (input.callStatus === 'calling') {
      return {
        status: 'waiting',
        label: 'Waiting',
        detail: 'Call setup is in progress. Transcript chunks will appear once audio starts.',
      };
    }
    return {
      status: 'missing',
      label: 'No Chunks',
      detail:
        'Active call has no transcript chunks yet. Check stream connection and microphone path.',
    };
  }

  const transcriptAt = Date.parse(input.latestTranscriptAt);
  if (Number.isNaN(transcriptAt)) {
    return {
      status: 'missing',
      label: 'No Chunks',
      detail: 'Latest transcript timestamp is invalid. Refresh feed and inspect timeline metadata.',
    };
  }

  const ageMs = Math.max(0, nowMs - transcriptAt);
  if (ageMs <= staleAfterMs) {
    return {
      status: 'live',
      label: 'Live',
      detail: 'Realtime transcript chunks are arriving within expected latency.',
    };
  }

  return {
    status: 'stale',
    label: 'Delayed',
    detail:
      'Transcript updates are lagging. Check stream finalization and fallback retry pressure.',
  };
}
